// AnalyticsBootstrap — mounts inside ConsentGate so it only runs after the
// user has granted Consumer Health Data consent. Re-verifies consent at
// mount time before loading any third-party SDK (defense in depth), then
// dynamically imports the hardened PostHog and Sentry clients.
//
// This is the ONLY place PostHog and Sentry are initialized in the app.
// The root route no longer touches them.

import { useEffect } from "react";
import { getSentryConfig } from "@/lib/sentry-config.functions";
import { getPostHogConfig } from "@/lib/posthog-config.functions";
import { hasActiveChdConsent } from "@/lib/consent-status";

export function AnalyticsBootstrap({ userId }: { userId: string }) {
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Defense in depth: confirm consent immediately before SDK load,
      // even though ConsentGate already gates the render tree.
      const ok = await hasActiveChdConsent(userId);
      if (cancelled || !ok) return;

      const [sentryRes, posthogRes] = await Promise.allSettled([
        getSentryConfig(),
        getPostHogConfig(),
      ]);
      if (cancelled) return;

      if (sentryRes.status === "fulfilled") {
        try {
          const { initSentryClient } = await import("@/lib/sentry-browser");
          await initSentryClient(sentryRes.value);
        } catch (e) { console.error("sentry import failed", e); }
      } else {
        console.error("getSentryConfig failed", sentryRes.reason);
      }

      if (posthogRes.status === "fulfilled") {
        try {
          const { initPostHogClient } = await import("@/lib/posthog-browser");
          await initPostHogClient(posthogRes.value);
        } catch (e) { console.error("posthog import failed", e); }
      } else {
        console.error("getPostHogConfig failed", posthogRes.reason);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  return null;
}
